import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { OrgSwitcherComponent, type OrgSwitcherOrg, type OrgSwitcherTeamSelection } from '@sbe-spinoff-client/app-shell';
import type { Org } from '../org-context.service';

/**
 * 헤더 좌측 단체 컨텍스트 스위처 — sms/commerce/timetable과 마찬가지로
 * `@sbe-spinoff-client/app-shell`의 순수 프레젠테이션 컴포넌트 `sbe-org-switcher`
 * (`OrgSwitcherComponent`)에 위임하는 얇은 wrapper다(`AccountMenuComponent`가
 * `sbe-profile-menu`를 감싸는 것과 동일 패턴, 2026-08-18 —
 * apps/schoolbell-e/plans/archive/2026-08-17-spinoff-auth-pattern-alignment.md 항목 4
 * 마무리. schoolbell-e/web이 SpinoffPlatform 세션을 실제로 쓰게 되면서 더 이상 이 lib을
 * 못 쓸 이유가 없어졌다).
 *
 * 이 wrapper가 담당하는 건 타입 매핑뿐이다 — `OrgContextService`의 `Org`(`orgId`/`name`/
 * `teams?`)를 공용 컴포넌트가 기대하는 `OrgSwitcherOrg`(`id`/`name`/`role?`/`teams?`)로
 * 맞춰준다. schoolbell-e는 org-role 배지 개념이 없어(현재 `Org`에 role 필드 자체가 없음)
 * `role`은 항상 undefined다. `teamId`는 `Org.teams`도 `OrgSwitcherOrg`도 이미 동일한
 * "{shard}:{id}" 문자열이라(2026-08-20 값공간 통일) 별도 변환 없이 그대로 통과시킨다.
 *
 * `personalContextEnabled`/`invitationsEnabled` 둘 다 끈다 — 개인 컨텍스트 항목은 의도적으로
 * 없고(사용자 지시, 2026-08-17 — 개인 신원 표시·전환은 우측 `app-account-menu`가 전담),
 * "받은 초대"는 schoolbell-e/web에 아직 org 초대 기능 자체가 없어 눌러도 아무 동작이
 * 없는 항목을 보여주지 않기 위해서다(`invitationsEnabled`는 이 wrapper 도입과 함께
 * `OrgSwitcherComponent`에 신설한 opt-out, `personalContextEnabled`와 동일 패턴).
 *
 * `teamsEnabled`는 이 wrapper가 그대로 통과시킨다(기본 false — opt-in, 2026-08-18) — `Org.teams`
 * 데이터 자체는 `GET /me/orgs`가 이미 내려주지만(`MyOrgDto.teams`), 노출 여부는 앱이 켜야 한다.
 * 켜면 team은 클릭 가능한 항목(`teamSelectionEnabled`)이 되어 `teamSelected`로 팀 뷰 진입을 알린다.
 */
@Component({
  selector: 'app-context-switcher',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OrgSwitcherComponent],
  template: `
    <sbe-org-switcher
      [orgs]="switcherOrgs()"
      [activeOrgId]="activeOrgId()"
      [personalContextEnabled]="false"
      [invitationsEnabled]="false"
      [teamsEnabled]="teamsEnabled()"
      [teamSelectionEnabled]="teamsEnabled()"
      (orgSelected)="handleOrgSelected($event)"
      (teamSelected)="handleTeamSelected($event)"
      (createOrgRequested)="createOrgRequested.emit()"
    />
  `,
})
export class ContextSwitcherComponent {
  readonly orgs = input<Org[]>([]);
  readonly activeOrgId = input<string | null>(null);
  readonly teamsEnabled = input<boolean>(false);

  readonly orgSelected = output<Org>();
  readonly teamSelected = output<{ orgId: string; teamId: string }>();
  readonly createOrgRequested = output<void>();

  protected readonly switcherOrgs = computed<OrgSwitcherOrg[]>(() =>
    this.orgs().map((org) => ({
      id: org.orgId,
      name: org.name,
      teams: org.teams,
    })),
  );

  // teamsEnabled면 team이 읽기 전용 배지가 아니라 클릭 가능한 항목이 된다 — 팀 뷰
  // (`/orgs/:orgId/teams/:teamId`)로 들어가는 유일한 진입점이다(사이드바에는 팀 목록이 없다).
  protected handleTeamSelected(selection: OrgSwitcherTeamSelection): void {
    this.teamSelected.emit({ orgId: selection.org.id, teamId: selection.teamId });
  }

  protected handleOrgSelected(switcherOrg: OrgSwitcherOrg): void {
    this.orgSelected.emit({ orgId: switcherOrg.id, name: switcherOrg.name });
  }
}
